import { currencyFormatter } from "@fla/common";
import BusinessIcon from "@fla/components/src/assets/svgs/business.svg";
import SellIcon from "@fla/components/src/assets/svgs/sell.svg";
import SplitIcon from "@fla/components/src/assets/svgs/split.svg";
import { entityOptions } from "@fla/components/src/definitions/asset";
import { Business_InitialState } from "@fla/components/src/interfaces";

import { EditIcon } from "../../../../SvgIcon";
import { useNames } from "../useNames";

type Props = { businessData: Business_InitialState[] } & ReturnType<typeof useNames>;

export function useBusiness({ businessData, spouseFirstName, userFirstName, getName }: Props) {
  const getValue = (data: any, vFor: string) => {
    if ((data.split_by === "sell" && data.sale_proceeds === "percentage") || data.split_by === "percentage") {
      return (Number(data.value) * Number(data.percentage[vFor])) / 100;
    } else {
      return Number(data.value) / 2;
    }
  };

  const totalAmount = (data: any, amountFor: string) => {
    if ([data.split_by, data.sale_proceeds].includes(amountFor)) {
      return data.value;
    } else return getValue(data, amountFor);
  };

  const userTotalAmount = (() => {
    return businessData
      .filter(
        data =>
          [data.split_by, data.sale_proceeds].includes("user") ||
          (data.split_by === "sell" && ["percentage", "50_50"].includes(data.sale_proceeds!)) ||
          data.split_by === "percentage"
      )
      .reduce((p, curr) => p + Number(totalAmount(curr, "user")), 0);
  })();

  const spouseTotalAmount = (() => {
    return businessData
      .filter(
        data =>
          [data.split_by, data.sale_proceeds].includes("spouse") ||
          (data.split_by === "sell" && ["percentage", "50_50"].includes(data.sale_proceeds!)) ||
          data.split_by === "percentage"
      )
      .reduce((p, curr) => p + Number(totalAmount(curr, "spouse")), 0);
  })();

  const ownCard = (val: "user" | "spouse", editData?: any) => {
    return Array.isArray(businessData) && businessData.length
      ? businessData
          .filter(data => [data.split_by, data.sale_proceeds].includes(val))
          .map((data, idx) => {
            const entity = entityOptions.find(et => et.value === data.entity)?.label;

            return (
              <article key={+idx} className="bg-white rounded-xl p-5 shadow-md mb-5">
                <section className="grid grid-cols-8 gap-y-4 md:gap-y-0 mb-2">
                  <div className="col-span-2 md:col-span-1">
                    <img src={BusinessIcon} className="w-12 h-auto my-3" alt="vehicle" />
                  </div>
                  <div className="col-span-6 md:col-span-5 space-y-1">
                    <p className="text-lg font-semibold">{data.name}</p>
                    <p className="text-sm">{entity}</p>
                    <p className="text-sm">
                      Current Owner:&nbsp;
                      <span className="font-medium">{getName(data.owner)}</span>
                    </p>
                  </div>
                  <div className="col-span-8 md:col-span-2 flex flex-col items-end">
                    <p className="text-xl">{currencyFormatter.format(Number(data.value))}</p>
                    <p className="text-gray-500 text-sm">Approx Value</p>
                    {typeof editData === "function" ? (
                      <button
                        className="transition-all hover:bg-primary/20 px-2 py-1 rounded-md text-sm text-primary font-bold uppercase mt-4 flex items-center space-x-2"
                        onClick={() => editData(data)}
                      >
                        <EditIcon classes="w-3.5 h-3.5" />
                        <p className="tracking-wide">Edit</p>
                      </button>
                    ) : null}
                  </div>
                </section>
                {data?.comment ? <p className="line-clamp-1 pl-11 text-sm">{data.comment}</p> : null}

                {data.split_by === "sell" ? (
                  <>
                    <hr />
                    <section className="flex justify-between items-center mt-4">
                      <div className="px-3 py-1 rounded-md bg-gray-300 flex items-center">
                        <p className="text-sm">To Sell</p>
                      </div>

                      {/* <div className="text-sm text-gray-500">
                            <p>
                              To {userFirstName}{" "}
                              {currencyFormatter.format(Number(data.value) / 2)}
                            </p>
                            <p>
                              To {spouseFirstName}{" "}
                              {currencyFormatter.format(Number(data.value) / 2)}
                            </p>
                          </div> */}
                    </section>
                  </>
                ) : null}
              </article>
            );
          })
      : null;
  };

  const splitCard = (val: "user" | "spouse", editData?: any) => {
    return Array.isArray(businessData) && businessData.length
      ? businessData
          .filter(
            data =>
              (data.split_by === "sell" && ["50_50", "percentage"].includes(data.sale_proceeds!)) ||
              data.split_by === "percentage"
          )
          .map((data, idx) => {
            const entity = entityOptions.find(et => et.value === data.entity)?.label;
            return (
              <article key={+idx} className="bg-white rounded-xl p-5 shadow-md mb-5">
                <section className="grid grid-cols-8 gap-y-4 md:gap-y-0 mb-2">
                  <div className="col-span-2 md:col-span-1">
                    <img src={BusinessIcon} className="w-12 h-auto my-3" alt="vehicle" />
                  </div>
                  <div className="col-span-6 md:col-span-5 space-y-1">
                    <p className="text-lg font-semibold">{data.name}</p>
                    <p className="text-sm">{entity}</p>
                    <p className="text-sm">
                      Current Value:&nbsp;
                      <span className="font-medium">{currencyFormatter.format(Number(data.value))}</span>
                    </p>

                    <p className="text-sm">
                      Current Owner:&nbsp;
                      <span className="font-medium">{getName(data.owner)}</span>
                    </p>
                  </div>
                  <div className="col-span-8 md:col-span-2 flex flex-col items-end">
                    <p className="text-xl">{currencyFormatter.format(getValue(data, val))}</p>
                    <p className="text-gray-500 text-sm">Approx Value</p>
                    {typeof editData === "function" ? (
                      <button
                        className="transition-all hover:bg-primary/20 px-2 py-1 rounded-md text-sm text-primary font-bold uppercase mt-4 flex items-center space-x-2"
                        onClick={() => editData(data)}
                      >
                        <EditIcon classes="w-3.5 h-3.5" />
                        <p className="tracking-wide">Edit</p>
                      </button>
                    ) : null}
                  </div>
                </section>
                {data?.comment ? <p className="line-clamp-1 pl-11 text-sm">{data.comment}</p> : null}
                <hr />
                <section className="flex justify-between items-center mt-4">
                  <div className="px-5 space-x-2 rounded-md bg-gray-300 flex items-center">
                    <img
                      src={[data.split_by].includes("percentage") ? SplitIcon : SellIcon}
                      className="w-4 h-auto my-2 stroke-current text-black"
                      alt="split/sell"
                    />
                    <p className="text-sm">{[data.split_by].includes("percentage") ? "To Split" : "To Sell"}</p>
                  </div>
                  {data.sale_proceeds === "50_50" ? (
                    <div className="text-sm text-gray-500">
                      <p>
                        50% To {userFirstName} ({currencyFormatter.format(Number(data.value) / 2)})
                      </p>
                      <p>
                        50% To {spouseFirstName} ({currencyFormatter.format(Number(data.value) / 2)})
                      </p>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      <p>
                        {data.percentage.user}% To {userFirstName} (
                        {currencyFormatter.format((Number(data.value) * Number(data.percentage.user)) / 100)})
                      </p>
                      <p>
                        {data.percentage.spouse}% To {spouseFirstName} (
                        {currencyFormatter.format((Number(data.value) * Number(data.percentage.spouse)) / 100)})
                      </p>
                    </div>
                  )}
                </section>
              </article>
            );
          })
      : null;
  };
  return {
    ownCard,
    splitCard,
    userTotalAmount,
    spouseTotalAmount,
    businessData
  };
}
